import { useCallback, useState } from 'react';

export type Vue = 'liste' | 'cartes';

export type PageVue = 'oeuvres' | 'artistes' | 'contacts';

/**
 * Vue (liste ou cartes) choisie sur une page : une préférence locale, par
 * compte et par navigateur, comme `preferences/bientotStorage.ts` — rien n'est
 * écrit côté serveur. Les clés gardent leur forme d'origine pour conserver les
 * choix déjà mémorisés.
 */
export function vueStorageKey(page: PageVue, compteId: string): string {
  const id = encodeURIComponent(compteId);
  return page === 'oeuvres' ? `artosera:oeuvres-vue:v1:${id}` : `artosera-${page}-vue:${id}`;
}

// Stockage refusé : le choix reste actif tant que la page vit.
const volatile = new Map<string, Vue>();

/** Vide les choix tenus en mémoire faute de stockage (pour les tests). */
export function oublierVuesVolatiles(): void {
  volatile.clear();
}

function estVue(valeur: unknown): valeur is Vue {
  return valeur === 'liste' || valeur === 'cartes';
}

/** Le choix du compte dans ce navigateur, ou `null` s'il n'en a pas fait. */
export function readVue(page: PageVue, compteId: string | null): Vue | null {
  if (!compteId || typeof window === 'undefined') return null;
  const cle = vueStorageKey(page, compteId);
  const enMemoire = volatile.get(cle);
  if (enMemoire) return enMemoire;
  try {
    const valeur = window.localStorage.getItem(cle);
    return estVue(valeur) ? valeur : null;
  } catch {
    return null;
  }
}

export function storeVue(page: PageVue, compteId: string, vue: Vue): void {
  const cle = vueStorageKey(page, compteId);
  try {
    window.localStorage.setItem(cle, vue);
    volatile.delete(cle);
  } catch {
    volatile.set(cle, vue);
  }
}

/**
 * La vue active : le choix enregistré du compte, sinon `defaut`. Lue une fois,
 * puis tenue en état.
 */
export function useVue(
  page: PageVue,
  compteId: string | null,
  defaut: Vue,
): [Vue, (vue: Vue) => void] {
  const [choix, setChoix] = useState<Vue | null>(() => readVue(page, compteId));
  const changer = useCallback(
    (vue: Vue): void => {
      setChoix(vue);
      if (compteId) storeVue(page, compteId, vue);
    },
    [page, compteId],
  );
  return [choix ?? defaut, changer];
}
